import { getEdgeItem } from "./fetchers";

/**
 * Grouped waiting-room configuration ("Option B").
 *
 * A *queue group* is a single waiting room that can front one OR many events.
 * All queue settings live once on the group; a `slugs` list maps event paths to
 * the group. This lets, say, 15 simultaneous on-sales share ONE line: a visitor
 * is gated once and can then move freely between any event in the group while
 * they hold an active slot.
 *
 * Stored in Edge Config under the key `queueGroups` (an object keyed by group id),
 * the same store the existing Vercel flags read from. For local / sandbox testing
 * set the QUEUE_CONFIG_JSON env var to a JSON object of the same shape, which
 * takes precedence when present.
 *
 * Example:
 *   {
 *     "summer-tour": {
 *       "enabled": true,
 *       "maxActive": 500,
 *       "sessionTtlSeconds": 600,
 *       "opensAt": "2026-07-01T09:00:00+12:00",
 *       "mode": "inline",
 *       "slugs": ["auckland/show-a", "auckland/show-b", "wellington/show-c"]
 *     }
 *   }
 *
 * A single-event queue is simply a group with one slug.
 *
 * Each slug must match the event path used in the URL after `/events/`, e.g.
 * `auckland/my-show` for `https://.../events/auckland/my-show`.
 */
export type QueueGroup = {
  /** Master switch for this group's waiting room. */
  enabled: boolean;
  /** Maximum number of users allowed through the gate at once (shared by the group). */
  maxActive: number;
  /**
   * How long an admitted session stays "active" before its slot is reclaimed,
   * in seconds. Doubles as the access-token lifetime and the average session
   * length used to estimate wait times.
   */
  sessionTtlSeconds: number;
  /**
   * How long before a waiting position is considered abandoned and can be reclaimed,
   * in seconds. Defaults to 120 seconds if not specified.
   */
  staleAfterSeconds?: number;
  /**
   * ISO 8601 timestamp when the queue arms. Before this moment the event pages
   * are fully browsable and the gate does nothing; at this moment the ticket
   * section flips into queue mode. If omitted, the queue is armed immediately
   * (as long as `enabled` is true).
   */
  opensAt?: string;
  /**
   * How the queue presents once armed:
   * - "inline" (default): only the ticket-purchase section enters queue mode;
   *   the rest of the event page stays browsable. The middleware does NOT
   *   redirect — gating happens in-page.
   * - "redirect": the whole page is gated; the middleware redirects un-passed
   *   visitors to the standalone /waiting-room page.
   */
  mode?: "inline" | "redirect";
  /** Event paths (after `/events/`) that belong to this group's queue. */
  slugs: string[];
};

/** Map of group id -> group config. */
export type QueueGroups = Record<string, QueueGroup>;

/** A group config paired with its resolved key (group id). */
export type ResolvedQueueGroup = { key: string; group: QueueGroup };

/** True once the queue is armed for this group (enabled + past opensAt). */
export function isQueueArmed(
  group: QueueGroup,
  now: number = Date.now()
): boolean {
  if (!group.enabled) return false;
  if (!group.opensAt) return true;
  const opens = Date.parse(group.opensAt);
  if (Number.isNaN(opens)) return true; // misconfigured time → fail open to armed
  return now >= opens;
}

export const QUEUE_ID_COOKIE = "itk_queue_id";

/**
 * Both pass and active cookies are scoped PER QUEUE GROUP (name includes the
 * group key) so a visitor can hold admissions to several concurrent on-sales
 * at once — admitting to group B must not clobber an existing pass for group A.
 */
export const QUEUE_PASS_COOKIE_PREFIX = "itk_qpass:";
/**
 * Client-readable (non-httpOnly) companion cookie. Its value is the group key
 * itself, so the client heartbeat knows which groups to keep alive. Never
 * trusted for access — the signed httpOnly pass cookie is the source of truth.
 */
export const QUEUE_ACTIVE_COOKIE_PREFIX = "itk_qactive:";

export const passCookieName = (groupKey: string) =>
  `${QUEUE_PASS_COOKIE_PREFIX}${groupKey}`;
export const activeCookieName = (groupKey: string) =>
  `${QUEUE_ACTIVE_COOKIE_PREFIX}${groupKey}`;

/**
 * Resolve every queue group. Resilient: returns {} on any failure so a
 * misconfigured Edge Config can never take down the site.
 */
export async function getQueueGroups(): Promise<QueueGroups> {
  // Env override wins — handy for sandbox/local testing without Edge Config.
  const envJson = process.env.QUEUE_CONFIG_JSON;
  if (envJson) {
    try {
      const parsed = JSON.parse(envJson);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as QueueGroups;
      }
    } catch {
      // fall through to Edge Config
    }
  }

  try {
    const cfg = await getEdgeItem<QueueGroups>("queueGroups", 5);
    return cfg && typeof cfg === "object" && !Array.isArray(cfg) ? cfg : {};
  } catch {
    return {};
  }
}

/**
 * Find the enabled queue group that owns a given event slug, if any.
 * Returns the group plus its key so callers can scope Redis/tokens to it.
 */
export async function getQueueGroupForSlug(
  slug: string
): Promise<ResolvedQueueGroup | null> {
  const groups = await getQueueGroups();
  for (const [key, group] of Object.entries(groups)) {
    if (!group.enabled || !Array.isArray(group.slugs)) continue;
    if (group.slugs.some((s) => normalizeSlug(s) === slug)) {
      return { key, group };
    }
  }
  return null;
}

/** Look up a group directly by its key (used after decoding a token). */
export async function getQueueGroupByKey(
  key: string
): Promise<ResolvedQueueGroup | null> {
  const groups = await getQueueGroups();
  const group = groups[key];
  return group && group.enabled ? { key, group } : null;
}

/**
 * Normalise an event path: drop `nocache` and `key-*` segments and trim slashes
 * so the value matches the slugs stored in config.
 */
export function normalizeSlug(raw: string): string {
  return raw
    .split("/")
    .filter((s) => s && s !== "nocache" && !s.startsWith("key-"))
    .join("/");
}

/** Extract the normalised event slug from a request pathname, or null. */
export function eventSlugFromPathname(pathname: string): string | null {
  const match = pathname.match(/^\/events\/(.+?)\/?$/);
  if (!match) return null;
  const slug = normalizeSlug(match[1]);
  return slug || null;
}

// ---------------------------------------------------------------------------
// Signed access tokens (HMAC-SHA256, edge-runtime safe via Web Crypto)
// ---------------------------------------------------------------------------

type TokenPayload = {
  /** queue-group key the token grants access to */
  s: string;
  /** visitor id */
  v: string;
  /** expiry, epoch seconds */
  e: number;
};

function getSecret(): string {
  const secret = process.env.QUEUE_TOKEN_SECRET;
  if (!secret) throw new Error("QUEUE_TOKEN_SECRET is not set");
  return secret;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++)
    binary += String.fromCharCode(bytes[i]);
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function base64UrlDecode(str: string): Uint8Array {
  const padded = str.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function hmac(data: string): Promise<Uint8Array> {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    "raw",
    enc.encode(getSecret()),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  const sig = await crypto.subtle.sign("HMAC", key, enc.encode(data));
  return new Uint8Array(sig);
}

function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}

/** Mint a signed access token granting `visitorId` entry to `groupKey`. */
export async function signAccessToken(
  groupKey: string,
  visitorId: string,
  ttlSeconds: number
): Promise<string> {
  const payload: TokenPayload = {
    s: groupKey,
    v: visitorId,
    e: Math.floor(Date.now() / 1000) + ttlSeconds,
  };
  const body = base64UrlEncode(
    new TextEncoder().encode(JSON.stringify(payload))
  );
  const sig = base64UrlEncode(await hmac(body));
  return `${body}.${sig}`;
}

/** Verify a token is well-formed, unexpired, and scoped to `groupKey`. */
export async function verifyAccessToken(
  token: string | undefined,
  groupKey: string
): Promise<boolean> {
  if (!token) return false;
  const parts = token.split(".");
  if (parts.length !== 2) return false;
  const [body, sig] = parts;

  try {
    const expected = await hmac(body);
    if (!timingSafeEqual(base64UrlDecode(sig), expected)) return false;

    const payload = JSON.parse(
      new TextDecoder().decode(base64UrlDecode(body))
    ) as TokenPayload;

    if (payload.s !== groupKey) return false;
    if (payload.e < Math.floor(Date.now() / 1000)) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Verify a token's signature + expiry and return its payload (groupKey + visitorId).
 * Unlike `verifyAccessToken` this is not scoped to a known group — it recovers the
 * group key from the token itself, for endpoints (release/heartbeat) that act on
 * whatever queue the holder currently has a pass for. Returns null if invalid.
 */
export async function readAccessToken(
  token: string | undefined
): Promise<{ groupKey: string; visitorId: string } | null> {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 2) return null;
  const [body, sig] = parts;

  try {
    const expected = await hmac(body);
    if (!timingSafeEqual(base64UrlDecode(sig), expected)) return null;

    const payload = JSON.parse(
      new TextDecoder().decode(base64UrlDecode(body))
    ) as TokenPayload;

    if (payload.e < Math.floor(Date.now() / 1000)) return null;
    return { groupKey: payload.s, visitorId: payload.v };
  } catch {
    return null;
  }
}

/**
 * Decode every valid pass cookie a visitor currently holds — one per queue
 * group they're admitted to. `cookieName` is returned alongside so callers can
 * refresh or clear the exact cookie it came from.
 */
export async function readAllAccessTokens(
  cookies: { name: string; value: string }[]
): Promise<{ groupKey: string; visitorId: string; cookieName: string }[]> {
  const passes = cookies.filter((c) =>
    c.name.startsWith(QUEUE_PASS_COOKIE_PREFIX)
  );
  const decoded = await Promise.all(
    passes.map(async (c) => {
      const token = await readAccessToken(c.value);
      return token ? { ...token, cookieName: c.name } : null;
    })
  );
  return decoded.filter((d): d is NonNullable<typeof d> => d !== null);
}

/**
 * Poll cadence: driven by estimated wait time rather than raw position, since
 * "N ahead" means very different things depending on a queue's maxActive.
 * Floored to the fastest tier whenever `ahead` is small — right at the front
 * is exactly when the estimate is least reliable (no completions recorded
 * yet, or a whole batch about to be admitted at once) and polling often
 * matters most.
 */
export function getPollInterval(
  ahead: number,
  estimatedWaitSeconds?: number | null
): number {
  let interval = 15000;
  if (ahead <= 10) {
    interval = 2000;
  } else if (estimatedWaitSeconds != null) {
    if (estimatedWaitSeconds <= 30) interval = 2000;
    else if (estimatedWaitSeconds <= 120) interval = 5000;
    else if (estimatedWaitSeconds <= 300) interval = 10000;
  } else if (ahead <= 100) {
    interval = 5000;
  } else if (ahead <= 200) {
    interval = 10000;
  }
  const jitter = Math.random() * 0.2 - 0.1; // ±10%
  interval = interval * (1 + jitter);
  return interval;
}

export function formatWait(
  seconds: number,
  t: (key: string, options?: Record<string, any>) => string
): string {
  if (seconds <= 60) return t("waitLessThanMinute");
  // currently, ux decision is to not show wait times more than 1 hour
  if (seconds >= 3600) return t("waitMoreThanOneHour");
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t("waitMinutes", { count: minutes });
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem
    ? t("waitHoursMinutes", { hours, minutes: rem })
    : t("waitHours", { count: hours });
}
